/**
 * PLT-7912: the tool trail. One gray line per assistant turn that says what the turn did
 * ("Searched the web, read DMA results and 5 more"), and that opens into a list of every tool the
 * turn used, in the order they ran. While the turn runs, the line shows the step it is on.
 *
 * A background read (`quiet: true`) has no card in the message; its row opens the card in place
 * ("Show card"). The cards that answer the user and the action cards stay in the message, drawn by
 * their own tool UIs; the trail only lists their tools.
 *
 * assistant-ui renders each tool-call part on its own, so the trail is drawn by the first trail part
 * of the message (`ToolTrailAnchor`), and reads the whole message from there. `withToolTrail` puts
 * the anchor in front of every registered tool UI, and `ToolTrailFallback` is the anchor for tools
 * that have no UI. Web chat only: MCP hosts (Claude, ChatGPT) draw their own tool calls.
 */
import {
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  useSyncExternalStore,
  type ComponentType,
  type SVGProps,
} from 'react';
import {
  makeAssistantToolUI,
  useMessage,
  type AssistantToolUI,
  type ToolCallMessagePartComponent,
  type ToolCallMessagePartProps,
} from '@assistant-ui/react';
import {
  Book1StreamlineCore,
  BuildingOfficeStreamlineCore,
  Cog1,
  Earth,
  EuroCircle,
  FileWithLines,
  GlobalWarming2StreamlineCore,
  GraphBarHorizontal,
  LightbulbOn,
  List,
  PencilSquare,
  SearchStreamlineCore,
} from '@upright/ui/icons';
import { getToolProgress, subscribeToolProgress } from './tool-progress-registry';
import {
  appNameOf,
  buildTrail,
  codeOf,
  isTrailOnlyResult,
  isTrailPart,
  isTrailToolName,
  minRunningMs,
  REASONING_TOOL_NAME,
  type TrailDetail,
  type TrailIcon,
  type TrailPart,
  type TrailStep,
} from './tool-trail-steps';
import { ChatMcpAppIframe } from './upright-chat-ui-common';
import styles from './tool-trail.module.css';

const ICONS: Record<TrailIcon, ComponentType<SVGProps<SVGSVGElement>>> = {
  search: SearchStreamlineCore,
  web: Earth,
  page: FileWithLines,
  knowledge: Book1StreamlineCore,
  data: GraphBarHorizontal,
  list: List,
  proposal: PencilSquare,
  code: Cog1,
  climate: GlobalWarming2StreamlineCore,
  company: BuildingOfficeStreamlineCore,
  financial: EuroCircle,
  reasoning: LightbulbOn,
  generic: Cog1,
};

const DETAIL_NOUN: Record<TrailDetail, string> = {
  card: 'card',
  code: 'code',
  sources: 'sources',
  reasoning: 'reasoning',
};

function Chevron() {
  return (
    <svg className={styles.chevron} width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M4.5 3 L7.5 6 L4.5 9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** The spinner the chat's tool status lines use (`.tool-status-spinner` in `upright-chat-theme.css`). */
function Spinner() {
  return <span className="tool-status-spinner" aria-hidden="true" />;
}

function useTrailParts(): TrailPart[] {
  const content = useMessage(state => state.content);
  return useMemo(() => content.filter(isTrailPart).map(part => part as unknown as TrailPart), [content]);
}

/**
 * The steps that already have their result but must still read as running, so a calculation that
 * returns at once does not look made up (`minRunningMs`). Only a step this trail saw running is held:
 * a reopened chat shows its steps done at once.
 */
function useHeldSteps(parts: readonly TrailPart[]): ReadonlySet<string> {
  const firstSeenRunning = useRef(new Map<string, number>());
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const now = Date.now();
  const held = new Set<string>();
  let releaseAt = Infinity;
  for (const part of parts) {
    const floor = minRunningMs(part.toolName);
    if (floor === 0) continue;
    const seenAt = firstSeenRunning.current.get(part.toolCallId);
    if (part.result === undefined) {
      if (seenAt === undefined) firstSeenRunning.current.set(part.toolCallId, now);
      continue;
    }
    if (seenAt !== undefined && seenAt + floor > now) {
      held.add(part.toolCallId);
      releaseAt = Math.min(releaseAt, seenAt + floor);
    }
  }
  useEffect(() => {
    if (releaseAt === Infinity) return;
    const timer = setTimeout(rerender, releaseAt - Date.now());
    return () => clearTimeout(timer);
  }, [releaseAt]);
  return held;
}

function resultData(result: unknown): Record<string, unknown> {
  if (!result || typeof result !== 'object') return {};
  const data = (result as { data?: unknown }).data ?? result;
  return typeof data === 'object' ? (data as Record<string, unknown>) : {};
}

function CodeDetail({ toolName, args, result }: { toolName: string; args: Record<string, unknown>; result: unknown }) {
  const data = resultData(result);
  const code = codeOf(toolName, args);
  const stdout = typeof data.stdout === 'string' ? data.stdout : '';
  // `execute_python` reports a sandbox failure (cap reached, sandbox unavailable) as `message`.
  const errorText =
    typeof data.stderr === 'string' && data.stderr ? data.stderr : typeof data.message === 'string' ? data.message : '';
  if (!code && !stdout && !errorText) return <div className={styles.note}>No code or output to show.</div>;
  return (
    <>
      {code && <pre className={styles.pre}>{code}</pre>}
      {stdout && <pre className={styles.pre}>{stdout}</pre>}
      {errorText && <pre className={styles.pre}>{errorText}</pre>}
    </>
  );
}

function SourcesDetail({ result }: { result: unknown }) {
  const citations = resultData(result).citations;
  const sources = Array.isArray(citations) ? (citations as Array<{ title?: string; url?: string }>) : [];
  const links = sources.filter((source): source is { title?: string; url: string } => typeof source.url === 'string');
  if (links.length === 0) return <div className={styles.note}>No results found.</div>;
  return (
    <div role="list" className={styles.sources}>
      {links.map((source, i) => (
        <div role="listitem" key={i}>
          <a href={source.url} target="_blank" rel="noopener noreferrer" className={styles.source}>
            {source.title || source.url}
          </a>
        </div>
      ))}
    </div>
  );
}

function StepDetail({
  step,
  part,
  reasoningText,
}: {
  step: TrailStep;
  part: TrailPart | undefined;
  reasoningText: string | null;
}) {
  switch (step.detail) {
    case 'card': {
      const appName = appNameOf(part?.result);
      if (!appName) return null;
      return <ChatMcpAppIframe bare key={step.toolCallId} appName={appName} toolResult={resultData(part?.result)} />;
    }
    case 'code':
      return <CodeDetail toolName={step.toolName} args={part?.args ?? {}} result={part?.result} />;
    case 'sources':
      return <SourcesDetail result={part?.result} />;
    case 'reasoning':
      return reasoningText ? (
        <div className={styles.reasoning}>{reasoningText}</div>
      ) : (
        <div className={styles.note}>The reasoning for this answer is hidden.</div>
      );
    case null:
      return null;
  }
}

function StepRow({
  step,
  part,
  reasoningText,
  open,
  onToggle,
}: {
  step: TrailStep;
  part: TrailPart | undefined;
  reasoningText: string | null;
  open: boolean;
  onToggle: () => void;
}) {
  const Icon = ICONS[step.icon];
  const noun = step.detail ? DETAIL_NOUN[step.detail] : null;
  return (
    <div role="listitem" className={styles.step}>
      <div className={styles.row}>
        <span className={styles.icon}>
          {step.state === 'running' ? <Spinner /> : <Icon width={14} height={14} aria-hidden="true" />}
        </span>
        <span className={styles.text}>
          <span className={styles.label}>{step.label}</span>
          {step.subject && <span className={styles.subject}>{step.subject}</span>}
        </span>
        {noun && (
          <button type="button" className={styles.rowToggle} aria-expanded={open} onClick={onToggle}>
            {open ? `Hide ${noun}` : `Show ${noun}`}
            <Chevron />
          </button>
        )}
      </div>
      {open && step.detail && (
        <div className={styles.detail}>
          <StepDetail step={step} part={part} reasoningText={reasoningText} />
        </div>
      )}
    </div>
  );
}

function ToolTrail() {
  const parts = useTrailParts();
  const runEnded = useMessage(state => state.status?.type !== 'running');
  const held = useHeldSteps(parts);
  const trail = buildTrail(parts, { runEnded, held });
  const currentId = trail.current?.toolCallId;
  // The live progress some long tools stream ("Assessed 3 of 7 sites…", PLT-5031).
  const progress = useSyncExternalStore(subscribeToolProgress, () =>
    currentId ? getToolProgress(currentId) : undefined,
  );
  // Starts closed, also after a reload: the trail is how the answer was reached, not the answer.
  const [open, setOpen] = useState(false);
  const [openDetails, setOpenDetails] = useState<ReadonlySet<string>>(() => new Set());

  if (!trail.current && !trail.summary) return null;

  const partsById = new Map(parts.map(part => [part.toolCallId, part]));
  const onlyStep =
    trail.toolStepCount === 1 ? trail.steps.find(step => step.toolName !== REASONING_TOOL_NAME) : undefined;
  const toggleDetail = (toolCallId: string) =>
    setOpenDetails(previous => {
      const next = new Set(previous);
      if (next.has(toolCallId)) next.delete(toolCallId);
      else next.add(toolCallId);
      return next;
    });

  return (
    <div className={styles.trail}>
      <button type="button" className={styles.toggle} aria-expanded={open} onClick={() => setOpen(!open)}>
        {trail.current ? (
          <>
            <Spinner />
            <span className={styles.line}>{progress ?? trail.current.liveText}</span>
          </>
        ) : onlyStep ? (
          <span className={styles.line}>
            {onlyStep.label}
            {onlyStep.subject && <span className={styles.subject}>{onlyStep.subject}</span>}
          </span>
        ) : (
          <span className={styles.line}>{trail.summary}</span>
        )}
        <Chevron />
      </button>
      {/* Roles rather than ol/li and divs rather than p: the chat styles every list and paragraph in an answer as markdown, indents included. */}
      {open && (
        <div role="list" className={styles.list}>
          {trail.steps.map(step => (
            <StepRow
              key={step.toolCallId}
              step={step}
              part={partsById.get(step.toolCallId)}
              reasoningText={trail.reasoningText}
              open={openDetails.has(step.toolCallId)}
              onToggle={() => toggleDetail(step.toolCallId)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Draws the trail when this tool call is the message's first trail part, and nothing otherwise.
 * Keyed by the call: assistant-ui keys content parts by index, so opening another saved chat would
 * otherwise hand this chat's open rows to the trail at the same place in that one (PLT-6891).
 */
function ToolTrailAnchor({ toolCallId }: { toolCallId: string }) {
  const isFirst = useMessage(state => {
    const first = state.content.find(isTrailPart);
    return first?.type === 'tool-call' && first.toolCallId === toolCallId;
  });
  return isFirst ? <ToolTrail key={toolCallId} /> : null;
}

/** The tool UI for a tool with no UI of its own: its row in the trail is all it shows. */
export const ToolTrailFallback: ToolCallMessagePartComponent = props => (
  <ToolTrailAnchor toolCallId={props.toolCallId} />
);

/**
 * Put the trail anchor in front of each tool UI whose tool is a trail step. A part a reopened chat
 * rebuilt from the call alone (`isTrailOnlyResult`) has no result to draw, so only its anchor renders.
 */
export function withToolTrail(toolUIs: AssistantToolUI[]): AssistantToolUI[] {
  return toolUIs.map(toolUI => {
    const { toolName, render: Render } = toolUI.unstable_tool;
    if (!isTrailToolName(toolName)) return toolUI;
    const WithTrail = (props: ToolCallMessagePartProps) => (
      <>
        <ToolTrailAnchor toolCallId={props.toolCallId} />
        {isTrailOnlyResult(props.result) ? null : <Render {...props} />}
      </>
    );
    return makeAssistantToolUI({ toolName, render: WithTrail });
  });
}
